<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * the colorRole/trackVariant class logic, and collapse effectiveProps to { ...base, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 circular progress indicator with color roles and track style variants.
 *
 * @status internal
 * @group status-feedback
 * @see https://quasar.dev/vue-components/circular-progress Quasar q-circular-progress API
 */
defineOptions({ name: 'HrCircularProgress' });

const props = defineProps({
  /** Current progress value. */
  value: {
    type: Number,
    required: true,
  },
  /** Maximum value for the progress calculation. */
  max: {
    type: Number,
    default: 100,
  },
  /**
   * Diameter of the ring, as a CSS length string (e.g. '32px').
   */
  size: {
    type: String,
    default: '48px',
  },
  /**
   * Stroke width as a fraction of the diameter (0 < x < 1).
   * M3-aligned default is 0.15; legacy callers override via legacyProps.
   */
  thickness: {
    type: Number,
    default: 0.15,
  },
  /**
   * When true, renders the default slot in the center of the ring.
   */
  showValue: {
    type: Boolean,
    default: false,
  },
  /** Disables transition animation so the indicator jumps immediately to the new value. */
  instantFeedback: {
    type: Boolean,
    default: false,
  },
  /**
   * Active indicator color role. Only applied under the M3 theme.
   * @values primary, success, on-surface
   */
  colorRole: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'success', 'on-surface'].includes(v),
  },
  /**
   * Track (background arc) style. Only applied under the M3 theme.
   * @values standard, subtle, transparent
   */
  trackVariant: {
    type: String,
    default: 'standard',
    validator: (v) => ['standard', 'subtle', 'transparent'].includes(v),
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    value: props.value,
    max: props.max,
    size: props.size,
    thickness: props.thickness,
    showValue: props.showValue,
    instantFeedback: props.instantFeedback,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});

const classes = computed(() => [
  'hr-circular-progress',
  `hr-circular-progress--${props.colorRole}`,
  `hr-circular-progress--track-${props.trackVariant}`,
]);
</script>

<template>
  <q-circular-progress
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
  >
    <!-- @slot default Content rendered in the center of the ring when `showValue` is true. -->
    <slot />
  </q-circular-progress>
</template>

<style lang="scss" scoped>
// ── Color role defaults ───────────────────────────────────────

.hr-circular-progress {
  --_active-color: var(--app-primary);
  --_track-color:  var(--app-surface-container-highest);
}

.hr-circular-progress--success {
  --_active-color: var(--app-success);
}

.hr-circular-progress--on-surface {
  --_active-color: var(--app-on-surface);
}

// ── Track variants ────────────────────────────────────────────

.hr-circular-progress--track-subtle {
  --_track-color: color-mix(in srgb, var(--_active-color) 20%, transparent);
}

.hr-circular-progress--track-transparent {
  --_track-color: transparent;
}

// ── SVG color overrides ───────────────────────────────────────

.hr-circular-progress :deep(.q-circular-progress__circle) {
  color: var(--_active-color);
}

.hr-circular-progress :deep(.q-circular-progress__track) {
  color: var(--_track-color);
}

// ── Center label typography ───────────────────────────────────

.hr-circular-progress :deep(.q-circular-progress__text) {
  font-size:      var(--app-type-label-small-size);
  font-weight:    var(--app-type-label-small-weight);
  line-height:    var(--app-type-label-small-line-height);
  letter-spacing: var(--app-type-label-small-tracking);
}
</style>
